import { useMemo, useCallback } from 'react';
import useSWR from 'swr';
import { permissionsService } from 'views/services/permissionsService';
import { useAdminsStore } from 'views/store/useAdminsStore';

export const usePermissions = () => {
  const { currentAdmin } = useAdminsStore();

  const {
    data: adminPermissions,
    error,
    isLoading,
  } = useSWR(
    currentAdmin ? '/api/admin/permissions/me' : null,
    () => permissionsService.getMyPermissions(),
    {
      refreshInterval: 5000,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
    }
  );

  const permissions = useMemo(() => {
    if (!adminPermissions?.permissions) return [];

    if (Array.isArray(adminPermissions.permissions)) {
      return adminPermissions.permissions;
    } else if (typeof adminPermissions.permissions === 'object') {
      return Object.entries(adminPermissions.permissions).map(
        ([resource, actions]) => ({
          resource,
          actions: Array.isArray(actions)
            ? actions
            : Object.keys(actions as Record<string, boolean>).filter(
                key => (actions as Record<string, boolean>)[key]
              ),
        })
      );
    }

    return [];
  }, [adminPermissions]);

  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      if (currentAdmin?.role === 'super_admin') return true;

      if (!Array.isArray(permissions)) return false;
      const permission = permissions.find(p => p.resource === resource);
      if (!permission) return false;
      return permission.actions.includes(action);
    },
    [currentAdmin, permissions]
  );

  const canRead = useCallback(
    (resource: string): boolean => hasPermission(resource, 'read'),
    [hasPermission]
  );
  const canCreate = useCallback(
    (resource: string): boolean => hasPermission(resource, 'create'),
    [hasPermission]
  );
  const canUpdate = useCallback(
    (resource: string): boolean => hasPermission(resource, 'update'),
    [hasPermission]
  );
  const canDelete = useCallback(
    (resource: string): boolean => hasPermission(resource, 'delete'),
    [hasPermission]
  );

  const hasAnyAccess = useCallback(
    (resource: string): boolean => {
      if (currentAdmin?.role === 'super_admin') return true;

      if (!Array.isArray(permissions)) return false;
      const permission = permissions.find(p => p.resource === resource);
      if (!permission) return false;
      return permission.actions.length > 0;
    },
    [currentAdmin, permissions]
  );

  const canEdit = (resource: string): boolean => {
    return canCreate(resource) || canUpdate(resource);
  };

  const canManage = (resource: string): boolean => {
    if (!Array.isArray(permissions)) return false;
    const permission = permissions.find(p => p.resource === resource);
    if (!permission) return false;
    return (
      permission.actions.includes('read') &&
      permission.actions.includes('create') &&
      permission.actions.includes('update') &&
      permission.actions.includes('delete')
    );
  };

  const getAccessibleResources = useMemo(() => {
    if (!Array.isArray(permissions)) return [];
    return permissions
      .filter(permission => hasAnyAccess(permission.resource))
      .map(p => p.resource);
  }, [permissions, hasAnyAccess]);

  const getReadableResources = useMemo(() => {
    if (!Array.isArray(permissions)) return [];
    return permissions
      .filter(permission => canRead(permission.resource))
      .map(p => p.resource);
  }, [permissions, canRead]);

  return {
    permissions,
    loading: isLoading,
    error,
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasAnyAccess,
    canEdit,
    canManage,
    getAccessibleResources,
    getReadableResources,
  };
};
